import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TextInput, Paper, Text, Group, Kbd, Box, UnstyledButton } from '@mantine/core';
import { MagnifyingGlass, Megaphone, Key, Lightbulb, Pulse, X } from '@phosphor-icons/react';
import api from '../utils/api';

const TYPE_CONFIG = {
  campaign:   { icon: Megaphone,  color: 'var(--mantine-color-blue-5)',  labelKey: 'campaign' },
  keyword:    { icon: Key,        color: 'var(--mantine-color-green-5)', labelKey: 'keyword' },
  suggestion: { icon: Lightbulb,  color: 'var(--mantine-color-yellow-5)', labelKey: 'suggestion' },
  activity:   { icon: Pulse,      color: 'var(--mantine-color-gray-5)',  labelKey: 'activity' },
};

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    if (q.length < 2) { setResults([]); return; }
    api.get(`/accounts/search/?q=${encodeURIComponent(q)}`)
      .then(res => setResults(res.data.results || []))
      .catch(() => setResults([]));
  }, []);

  const handleChange = (val) => {
    setQuery(val);
    setActiveIndex(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 200);
  };

  const handleSelect = (result) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(result.url);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    }
  };

  const showDropdown = open && query.length >= 2;

  return (
    <div ref={containerRef} className="relative flex-1">
      <TextInput
        ref={inputRef}
        placeholder={t('shell.search.placeholder')}
        leftSection={<MagnifyingGlass size={16} className="text-gray-400" />}
        rightSection={
          query ? (
            <UnstyledButton onClick={() => { setQuery(''); setResults([]); }} style={{ display: 'flex' }}>
              <X size={14} className="text-gray-400" />
            </UnstyledButton>
          ) : (
            <Kbd size="xs" style={{ fontSize: 10, padding: '1px 5px', lineHeight: '16px' }}>⌘K</Kbd>
          )
        }
        value={query}
        onChange={(e) => handleChange(e.currentTarget.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        size="sm"
        styles={{
          input: {
            backgroundColor: 'var(--mantine-color-gray-0)',
            border: '1px solid var(--mantine-color-gray-2)',
            '&:focus': { borderColor: 'var(--mantine-color-brand-5)' },
          },
        }}
      />
      {showDropdown && (
        <Paper
          shadow="lg"
          radius="md"
          withBorder
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {results.length === 0 ? (
            <Box p="md">
              <Text size="sm" c="dimmed" ta="center">{t('shell.search.noResults', { query })}</Text>
            </Box>
          ) : (
            <div style={{ padding: 4 }}>
              {results.map((result, i) => {
                const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.activity;
                const Icon = config.icon;
                return (
                  <UnstyledButton
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setActiveIndex(i)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      backgroundColor: i === activeIndex ? 'var(--mantine-color-gray-0)' : 'transparent',
                      transition: 'background-color 0.1s',
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Icon size={16} weight="bold" style={{ color: config.color, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} truncate>{result.title}</Text>
                        <Group gap={6}>
                          <Text size="xs" c="dimmed" truncate>{result.subtitle}</Text>
                          <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>{t(`shell.search.types.${config.labelKey}`)}</Text>
                        </Group>
                      </div>
                    </Group>
                  </UnstyledButton>
                );
              })}
            </div>
          )}
        </Paper>
      )}
    </div>
  );
}
